'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import TagInfoCard, { hasTagInfoCardData, type TagInfoCardData } from '@/components/tag/TagInfoCard'
import { getApiBaseUrl } from '@/lib/config'
import { getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'

const tagInfoCache = new Map<string, TagInfoCardData | null>()

export function seedTagInfoCache(preloaded: Record<string, TagInfoCardData>): void {
  for (const [slug, data] of Object.entries(preloaded)) {
    tagInfoCache.set(slug.toLowerCase(), data)
  }
}

const INFO_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM7.25 6.75V5.5h1.5v1.25h-1.5Zm0 4.5v-3.5h1.5v3.5h-1.5Z"/></svg>`

function slugFromElement(el: HTMLElement): string | null {
  const fromData = el.dataset.tagSlug?.trim()
  if (fromData) return fromData.toLowerCase()
  return null
}

async function fetchTagInfo(slug: string): Promise<TagInfoCardData | null> {
  const cached = tagInfoCache.get(slug)
  if (cached !== undefined) return cached

  try {
    const res = await fetch(`${getApiBaseUrl()}/api/news/tag/slug/${encodeURIComponent(slug)}`)
    if (!res.ok) {
      tagInfoCache.set(slug, null)
      return null
    }
    const tag = await res.json()
    const rawImage = tag?.tag_image ?? tag?.tagImage ?? null
    const data: TagInfoCardData = {
      slug,
      name: tag?.tag || slug,
      image: rawImage,
      imageUrl: rawImage ? getImageUrl(rawImage, IMAGE_PRESETS.tagInfo) : null,
      content: tag?.tag_content ?? tag?.tagContent ?? null,
    }
    const result = hasTagInfoCardData(data) ? data : null
    tagInfoCache.set(slug, result)
    return result
  } catch {
    tagInfoCache.set(slug, null)
    return null
  }
}

function ensureInfoButton(link: HTMLAnchorElement): HTMLButtonElement | null {
  if (!link.classList.contains('news-inline-tag-has-info')) return null

  const slug = slugFromElement(link)
  if (!slug || !link.isConnected) return null

  const existingWrap = link.closest('.news-inline-tag-wrap')
  if (existingWrap) {
    return existingWrap.querySelector<HTMLButtonElement>('.news-inline-tag-info-btn')
  }

  const parent = link.parentNode
  if (!parent) return null

  const wrap = document.createElement('span')
  wrap.className = 'news-inline-tag-wrap'
  wrap.dataset.tagInfoUi = '1'
  parent.insertBefore(wrap, link)

  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'news-inline-tag-info-btn'
  btn.dataset.tagSlug = slug
  btn.setAttribute('aria-label', `${link.textContent?.trim() || slug} hakkında bilgi`)
  btn.setAttribute('aria-expanded', 'false')
  btn.innerHTML = INFO_ICON_SVG

  wrap.appendChild(btn)
  wrap.appendChild(link)

  return btn
}

type ArticleInlineTagHoverProps = {
  containerSelector: string
  preloadedTagInfos?: Record<string, TagInfoCardData>
}

export default function ArticleInlineTagHover({
  containerSelector,
  preloadedTagInfos,
}: ArticleInlineTagHoverProps) {
  const [active, setActive] = useState<{
    info: TagInfoCardData
    rect: DOMRect
    slug: string
  } | null>(null)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)
  const activeButtonRef = useRef<HTMLButtonElement | null>(null)
  const activeSlugRef = useRef<string | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const setupRunningRef = useRef(false)

  const closePopover = useCallback(() => {
    activeButtonRef.current?.setAttribute('aria-expanded', 'false')
    activeButtonRef.current = null
    activeSlugRef.current = null
    setActive(null)
    setLoadingSlug(null)
  }, [])

  useEffect(() => {
    if (preloadedTagInfos) seedTagInfoCache(preloadedTagInfos)
  }, [preloadedTagInfos])

  useEffect(() => {
    const container = document.querySelector(containerSelector)
    if (!container) return

    const boundButtons = new WeakSet<HTMLButtonElement>()
    const cleanups: Array<() => void> = []

    const bindInfoButton = (btn: HTMLButtonElement) => {
      if (boundButtons.has(btn)) return
      boundButtons.add(btn)

      const toggle = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        const slug = slugFromElement(btn)
        if (!slug) return

        if (activeSlugRef.current === slug) {
          closePopover()
          return
        }

        activeButtonRef.current = btn
        activeSlugRef.current = slug
        btn.setAttribute('aria-expanded', 'true')
        const rect = btn.getBoundingClientRect()

        const cached = tagInfoCache.get(slug)
        if (cached !== undefined) {
          if (!cached) {
            closePopover()
            return
          }
          setActive({ info: cached, rect, slug })
          return
        }

        setLoadingSlug(slug)
        const info = await fetchTagInfo(slug)
        setLoadingSlug(null)

        if (activeButtonRef.current !== btn) return
        if (!info) {
          closePopover()
          return
        }
        setActive({ info, rect: btn.getBoundingClientRect(), slug })
      }

      btn.addEventListener('click', toggle)
      cleanups.push(() => btn.removeEventListener('click', toggle))
    }

    const setupLinks = () => {
      if (setupRunningRef.current) return
      setupRunningRef.current = true
      try {
        const links = container.querySelectorAll<HTMLAnchorElement>('a.news-inline-tag-has-info')
        links.forEach((link) => {
          if (link.closest('.news-inline-tag-wrap')) {
            const btn = link
              .closest('.news-inline-tag-wrap')
              ?.querySelector<HTMLButtonElement>('.news-inline-tag-info-btn')
            if (btn) bindInfoButton(btn)
            return
          }
          const btn = ensureInfoButton(link)
          if (btn) bindInfoButton(btn)
        })
      } finally {
        setupRunningRef.current = false
      }
    }

    setupLinks()

    let observerFrame = 0
    const observer = new MutationObserver(() => {
      if (observerFrame) cancelAnimationFrame(observerFrame)
      observerFrame = requestAnimationFrame(() => {
        observerFrame = 0
        setupLinks()
      })
    })
    observer.observe(container, { childList: true, subtree: true })

    const onDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (popoverRef.current?.contains(target)) return
      if (activeButtonRef.current?.contains(target)) return
      if ((target as HTMLElement).closest?.('.news-inline-tag-info-btn')) return
      closePopover()
    }

    document.addEventListener('pointerdown', onDocumentPointerDown)
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePopover()
    }
    document.addEventListener('keydown', onEscape)

    return () => {
      if (observerFrame) cancelAnimationFrame(observerFrame)
      observer.disconnect()
      cleanups.forEach((fn) => fn())
      document.removeEventListener('pointerdown', onDocumentPointerDown)
      document.removeEventListener('keydown', onEscape)
      closePopover()
    }
  }, [closePopover, containerSelector])

  if (!active && !loadingSlug) return null

  const popoverWidth = 300
  const gap = 8
  const anchorRect = active?.rect
  const spaceBelow = anchorRect
    ? window.innerHeight - anchorRect.bottom - gap
    : 0
  const showAbove = anchorRect ? spaceBelow < 240 && anchorRect.top > 260 : false
  const top = anchorRect
    ? showAbove
      ? anchorRect.top - gap
      : anchorRect.bottom + gap
    : 0
  const left = anchorRect
    ? Math.min(
        Math.max(12, anchorRect.left + anchorRect.width / 2 - popoverWidth / 2),
        window.innerWidth - popoverWidth - 12,
      )
    : 12

  return (
    <div
      ref={popoverRef}
      className="fixed z-[120]"
      style={{
        top: anchorRect ? top : '50%',
        left,
        width: popoverWidth,
        transform: anchorRect && showAbove ? 'translateY(-100%)' : undefined,
      }}
      role="dialog"
      aria-label={active ? `${active.info.name} hakkında` : 'Etiket bilgisi yükleniyor'}
    >
      {loadingSlug && !active ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center text-xs text-gray-500 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-gray-400">
          Yükleniyor…
        </div>
      ) : active ? (
        <TagInfoCard {...active.info} variant="popover" />
      ) : null}
    </div>
  )
}
